import typeCheck from 'common/util/typeCheck';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { COMMISSION_TYPE_FLAT_RATE, COMMISSION_TYPE_PERCENTAGE } from 'common/financeConstants';

import { roundTwoDecimals } from 'common/formatters';

/**
 * Calculates an invoice's due date. The formula is: Lease start date + invoice payout policy (if any).
 *
 * @param  {DateString} options.leaseStartDate      Valid ISOString of the lease start date.
 * @param  {Number}     options.invoicePayoutPolicy The merchant's invoice payout policy.
 * @return {Moment}     Moment instance of the invoice's due date.
 */
export function calculateInvoiceDueDate({ leaseStartDate, invoicePayoutPolicy = 0 }) {
  typeCheck('leaseStartDate::DateString', leaseStartDate);
  typeCheck('invoicePayoutPolicy::Number', invoicePayoutPolicy);
  // TODO(@d-oliveros): Add month offset?
  return momentWithAustinTimezone(leaseStartDate).add(invoicePayoutPolicy, 'days');
}

/**
 * Calculates an apartment locator's commion due date.
 * The formula is: Lease start date + invoice payout policy + month offset.
 *
 * @param  {DateString} options.leaseStartDate      Valid ISOString of the lease start date.
 * @param  {Number}     options.invoicePayoutPolicy The merchant's invoice payout policy.
 * @return {Moment}     Moment instance of the commission's due date.
 */
export function calculateApartmentLocatorCommissionDueDate({
  leaseStartDate,
  invoicePayoutPolicy,
}) {
  typeCheck('leaseStartDate::DateString', leaseStartDate);
  typeCheck('invoicePayoutPolicy::Number', invoicePayoutPolicy);
  // TODO(@d-oliveros): Add month offset, ask Joel about it.
  return momentWithAustinTimezone(leaseStartDate).add(invoicePayoutPolicy, 'days');
}

/**
 * Calculates a commission's amount.
 *
 * @param  {number} options.rentPrice            The base rent price.
 * @param  {number} options.commissionPercentage The commission's percentage cut.
 * @param  {number} options.commissionFlatRate   The commission's flat rate amount.
 * @return {Object} Object with the resulting amount owed and the commission type.
 */
export function calculateCommissionAmount({ rentPrice, commissionPercentage, commissionFlatRate }) {
  typeCheck('rentPrice::PositiveNumber', rentPrice);
  typeCheck('commissionPercentage::Maybe PositiveNumber', commissionPercentage);
  typeCheck('commissionFlatRate::Maybe PositiveNumber', commissionFlatRate);

  if (!!commissionPercentage === !!commissionFlatRate) {
    // xor
    const error = new Error(
      '[calculateCommissionAmount] Accepts commissionPercentage or ' +
        'commissionFlatRate, but not both.',
    );
    error.name = 'SrCalculateInvoiceAmountDueInvalidArgumentsError';
    error.details = {
      rentPrice,
      commissionPercentage,
      commissionFlatRate,
    };
    throw error;
  }

  if (commissionFlatRate) {
    return {
      commissionType: COMMISSION_TYPE_FLAT_RATE,
      commissionAmount: commissionFlatRate,
      commissionPercentage,
      commissionFlatRate,
    };
  }

  return {
    commissionType: COMMISSION_TYPE_PERCENTAGE,
    commissionAmount: roundTwoDecimals(rentPrice * commissionPercentage),
    commissionPercentage,
    commissionFlatRate,
  };
}

/**
 * Calculates a commission's amount based on the partner's billing fees.
 *
 * @param  {number} rentPrice    The listing's rent price.
 * @param  {number} daysOnMarket The days a listing has been on market.
 * @param  {Object} billingFees  The listing's billing fees.
 * @return {Object}              Commission fields
 */
export function calculateCommissionAmountFromBillingFees({
  rentPrice,
  daysOnMarket,
  householdListing,
  billingFees = {},
}) {
  const {
    leaseFeePercentage,
    leaseFeeMinAmount,
    leaseFeeMaxAmount,
    discountLongDOM,
    longDOMThreshold,
    longDOMLeaseFeePercentage,
    leaseFeeThirdPartyAccepted,
    leaseFeeThirdPartyPercentage,
  } = billingFees;

  typeCheck('householdListing::NonEmptyObject', householdListing);

  // Handle long DOM discount
  if (discountLongDOM && daysOnMarket >= longDOMThreshold) {
    const commissionCalculations = calculateCommissionAmount({
      rentPrice,
      commissionPercentage: longDOMLeaseFeePercentage,
    });
    return {
      ...commissionCalculations,
      isLongDOM: true,
      note: `Discounted because over ${longDOMThreshold} days on market`,
    };
  }

  const useThirdPartyPercentage = leaseFeeThirdPartyAccepted && householdListing.hasBuyersAgent;
  const note = useThirdPartyPercentage
    ? `Leasing Fee with Agent: ${householdListing.buyersAgentName}`
    : null;

  const commissionCalculations = calculateCommissionAmount({
    rentPrice,
    commissionPercentage: useThirdPartyPercentage
      ? leaseFeeThirdPartyPercentage
      : leaseFeePercentage,
  });

  if (commissionCalculations.commissionAmount < leaseFeeMinAmount) {
    const flatRateCommissionCalculations = calculateCommissionAmount({
      rentPrice,
      commissionFlatRate: leaseFeeMinAmount,
    });
    return {
      ...flatRateCommissionCalculations,
      isLongDOM: false,
      note,
    };
  }

  if (
    !!leaseFeeMaxAmount &&
    commissionCalculations.commissionAmount > leaseFeeMaxAmount &&
    !useThirdPartyPercentage
  ) {
    const flatRateCommissionCalculations = calculateCommissionAmount({
      rentPrice,
      commissionFlatRate: leaseFeeMaxAmount,
    });
    return {
      ...flatRateCommissionCalculations,
      isLongDOM: false,
      note,
    };
  }

  return {
    ...commissionCalculations,
    isLongDOM: false,
    note,
  };
}

export function calculateRenewalInvoiceAmount({ rentPrice, billingFees }) {
  let invoiceAmount = roundTwoDecimals(rentPrice * billingFees.renewalFeePercentage);

  if (!!billingFees.renewalFeeMinAmount && invoiceAmount < billingFees.renewalFeeMinAmount) {
    invoiceAmount = roundTwoDecimals(billingFees.renewalFeeMinAmount);
  }

  return invoiceAmount;
}
